/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren } from 'preact';
import { useState } from 'preact/hooks';
import { classNames } from '../utils/UNSAFE_classNames';
import { Keys, containsKey } from '../utils/UNSAFE_keys';
import { useId } from '../hooks/UNSAFE_useId';
import { ToggleDetail } from '../UNSAFE_Collection';
import { useViewportIntersect, ViewportConfig } from '../hooks/UNSAFE_useViewportIntersect';
import { TabbableModeContext } from '../hooks/UNSAFE_useTabbableMode';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { useInteractionStyle } from '../hooks/UNSAFE_useInteractionStyle';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';
import { flexitemInterpolations } from '../utils/UNSAFE_interpolations/flexitem';
import type { FlexitemProps } from '../utils/UNSAFE_interpolations/flexitem';
import { GroupedListStyles } from 'src/UNSAFE_GroupedList/themes/GroupedListStyles.css';
import { listGroupHeaderMultiVariantStyles } from './themes/ListStyles.css';
import { GroupedListRedwoodTheme } from '../UNSAFE_GroupedList/themes/redwood/GroupedListTheme';

// default style class for items in ListView
export const GROUP_SELECTOR = '[data-oj-listview-group]';
export const STICKY_SELECTOR = '[data-oj-listview-sticky]';

/**
 * Props for the ListViewItem Component
 */
export type Props<K> = {
  children: ComponentChildren;
  itemKey: K;
  itemIndex: number;
  itemDepth: number;
  isFocused: boolean;
  isFocusRingVisible: boolean;
  isActive: boolean;
  isGridlineVisible: boolean;
  isExpandable: boolean;
  expandedKeys: Keys<K>;
  ariaDescribedBy?: string;
  onToggle?: (detail: ToggleDetail<K>) => void;
  viewportConfig?: ViewportConfig;
};

/**
 * The internal component used to render a group header in ExpandableList and GroupedList.
 */
export function ListGroupHeader<K extends string | number>({
  ariaDescribedBy,
  children,
  itemKey,
  itemIndex,
  itemDepth,
  isFocused,
  isFocusRingVisible,
  isActive,
  isGridlineVisible,
  isExpandable,
  expandedKeys,
  viewportConfig
}: Props<K>) {
  const [isStuck, setStuck] = useState(false);

  const { classes: groupedListThemeClasses, styles: groupedListStyles } = useComponentTheme<
    never,
    GroupedListStyles
  >(GroupedListRedwoodTheme);

  const { interactionProps, applyHoverStyle, applyPseudoHoverStyle, applyActiveStyle } =
    useInteractionStyle();

  const variantClasses = listGroupHeaderMultiVariantStyles({
    expandable: isExpandable ? 'isExpandable' : 'notExpandable',
    needsEventsHover: applyHoverStyle ? 'isNeedsEventsHover' : 'notNeedsEventsHover',
    pseudoHover: applyPseudoHoverStyle ? 'isPseudoHover' : 'notPseudoHover',
    active: applyActiveStyle || isActive ? 'isActive' : 'notActive',
    focusRingVisible:
      isFocusRingVisible && isFocused ? 'isFocusRingVisible' : 'notFocusRingVisible',
    gridlineTop: itemIndex > 0 ? 'visible' : 'hidden',
    gridlineBottom: isGridlineVisible ? 'visible' : 'hidden'
  });

  const baseClasses = [variantClasses];
  if (isStuck) {
    baseClasses.push(groupedListThemeClasses);
    baseClasses.push(groupedListStyles.stuckHeader);
  }

  const classes = classNames(baseClasses);
  const styleInterpolations = mergeInterpolations<FlexitemProps>([
    ...Object.values(flexitemInterpolations)
  ]);
  const { class: cls, ...cellStyles } = styleInterpolations({
    alignSelf: 'center',
    flex: '1 0 auto'
  });

  useViewportIntersect(
    {
      scroller: () => {
        if (isExpandable) {
          return null;
        }
        if (viewportConfig == null) {
          return document.body;
        }
        return viewportConfig.scroller();
      }
    },
    0,
    1,
    '[data-oj-key=' + itemKey + ']',
    () => {
      setStuck(false);
    },
    (entry: IntersectionObserverEntry) => {
      if (entry.boundingClientRect.y !== entry.intersectionRect.y) {
        setStuck(true);
      }
    }
  );
  return (
    <div
      id={useId()}
      role="row"
      aria-rowindex={itemIndex + 1}
      aria-level={isNaN(itemDepth) ? undefined : itemDepth + 1}
      data-oj-key={itemKey}
      class={classes}
      {...(typeof itemKey === 'number' && { 'data-oj-key-type': 'number' })}
      {...interactionProps}>
      <div
        id={useId()}
        role="gridcell"
        aria-describedby={ariaDescribedBy}
        aria-colindex={1}
        aria-expanded={containsKey(expandedKeys, itemKey)}
        style={cellStyles}>
        <TabbableModeContext.Provider value={{ isTabbable: false }}>
          {children}
        </TabbableModeContext.Provider>
      </div>
    </div>
  );
}
